import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Globe, Zap, Shield } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, user } = useAuth();

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/site/dashboard');
    }
  }, [user, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate('/site/dashboard');
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const features = [
    { icon: Globe, text: 'Create unlimited websites' },
    { icon: Zap, text: 'Lightning fast performance' },
    { icon: Shield, text: 'Secure & reliable hosting' },
    { icon: Sparkles, text: 'AI-powered content' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-4">
              <Globe className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Hostbit Cloud</h1>
            <p className="text-gray-500 mt-2">No-Code Website Builder</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-500 text-center mb-6">
              Sign in to continue building your websites
            </p>

            {/* Google Sign In */}
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
            >
              <FcGoogle className="w-5 h-5" />
              Continue with Google
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Admin Login Link */}
            <button
              onClick={() => navigate('/site/admin-login')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors font-medium"
            >
              <Shield className="w-5 h-5" />
              Admin Login
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-6">
            By signing in, you agree to our{' '}
            <a href="#" className="text-primary-600 hover:underline">Terms</a>
            {' '}and{' '}
            <a href="#" className="text-primary-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>

      {/* Right Side - Features */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary-600 via-primary-700 to-purple-700 text-white p-12 flex-col justify-center">
        <div className="max-w-lg">
          <h2 className="text-4xl font-bold mb-6">
            Build Stunning Websites Without Code
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Create professional websites in minutes with our intuitive drag-and-drop builder. 
            No coding skills required.
          </p>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <feature.icon className="w-5 h-5" />
                </div>
                <span className="text-lg">{feature.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-white/10 rounded-xl backdrop-blur-sm">
            <p className="text-sm text-primary-100 mb-2">Trusted by 10,000+ users worldwide</p>
            <div className="flex gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 border-2 border-white"
                  />
                ))}
              </div>
              <div className="flex items-center">
                <span className="text-yellow-400">★★★★★</span>
                <span className="ml-2 text-sm">4.9/5 rating</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
